import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Config } from 'olympe';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';
import { useDebounce } from '../../shared/_helpers';
import { getApplicationContext } from '../../../common/utils/Context';

export const UserDropdown = ({
  id,
  isUserDDDisabled,
  limitCheck = 2,
  label,
  context,
  handleChange,
  selectedUser,
  columnName,
}) => {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [noOptionTextVal, seNoOptionTextVal] = useState('No Options');

  const debouncedTextInput = useDebounce(inputText, 500);
  const applicationContext = getApplicationContext(context);
  const graphToken = applicationContext.get('graphAPIToken');

  const formatLabel = (name, email) => {
    return `${name} (${email})`;
  };

  const getLabel = (option) => {
    if (typeof option === 'string' && option.length > 0) {
      const userDetailsArr = option.split(';');
      if (userDetailsArr.length === 1) {
        return userDetailsArr[0];
      } else return userDetailsArr[0] + ` (${userDetailsArr.at(-1)})`;
    } else if (
      typeof option === 'object' &&
      option?.displayName &&
      option?.mail
    ) {
      return formatLabel(option.displayName, option.mail);
    } else return '';
  };

  useEffect(async () => {
    if (debouncedTextInput?.length >= limitCheck) {
      const options = await fetchUsers(debouncedTextInput);
      setOptions(options.value);
      seNoOptionTextVal('No options');
    } else {
      seNoOptionTextVal('No options');
      setOptions([]);
    }
  }, [debouncedTextInput]);

  const fetchUsers = async (searchText) => {
    setLoading(true);
    const url =
      Config.getParameter('ms_graph_path') +
      `/users?$search="displayName:${searchText}" OR "mail:${searchText}"&$orderby=displayName&$count=true`;
    try {
      const result = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${graphToken}`,
          ConsistencyLevel: 'eventual',
        },
      });

      if (!result.ok) {
        return {
          error: true,
          message: `Error ${result.status}`,
        };
      } else {
        const jsonResult = await result.json();
        return jsonResult;
      }
    } catch (error) {
      return {
        error: true,
        message: error.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (newSelection) => {
    const userName =
      newSelection && Object.entries(newSelection).length > 0
        ? `${newSelection.displayName};${newSelection.mail}`
        : '';
    handleChange(userName, columnName);
    setOpen(false);
  };

  const handleOpen = async () => {
    setOpen(true);
    if (!options.length && debouncedTextInput?.length >= limitCheck) {
      const options = await fetchUsers(debouncedTextInput);
      setOptions(options.value);
    }
  };

  const handleInputChange = (value, reason) => {
    if (reason === 'reset') {
      const regex = /\(([^)]+)\)/;
      const match = regex.exec(value);
      if (match) {
        setInputText(match[1]);
      } else setInputText(value);
    }
    // extract email id from parenthesis for all the previously saved stakeholder values
    else setInputText(value);
  };

  return (
    <Autocomplete
      id={id}
      clearText='Clear'
      closeText='Close'
      noOptionsText={noOptionTextVal}
      options={options}
      value={selectedUser}
      disabled={isUserDDDisabled || false}
      getOptionLabel={(option) => getLabel(option)}
      loading={loading}
      loadingText={'Loading...'}
      onChange={(e, v) => {
        handleOnChange(v);
      }}
      onPaste={(e, v) => {
        const parsedText = e.clipboardData.getData('text');
        // trigger processing of pasted values list, or leave pasted value as is if is not a list that is pasted
        if (parsedText.includes(';')) {
          setPastedText(parsedText);
        }
      }}
      open={open}
      onOpen={handleOpen}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={() => {
        setOpen(false);
      }}
      onInputChange={(event, value, reason) => {
        handleInputChange(value, reason);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || 'Select User'}
          variant='outlined'
        />
      )}
    />
  );
};

UserDropdown.propTypes = {
  label: PropTypes.string,
  isUserDDDisabled: PropTypes.bool,
  id: PropTypes.string,
  limitTags: PropTypes.number,
  selectedUser: PropTypes.string,
  columnName: PropTypes.string,
};
